import React from 'react';
import styled from 'styled-components';
import { FaLinkedin } from 'react-icons/fa';
import SiteHeader from '../components/SiteHeader';
import SiteFooter from '../components/SiteFooter';
import { PolicyPage } from '../styles/policyStyles';
import { FONT_SANS } from '../constants';
import { colors } from '../theme/colors';
import { useLanguage } from '../context/LanguageContext';

const Subtitle = styled.p`
  font-size: 18px;
  color: ${colors.inkSoft};
`;

const StageList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 12px 0 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const StageItem = styled.li`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 14px 16px;
  border: 1px solid ${colors.border};
  border-left: 3px solid ${colors.accent};
  border-radius: 12px;
  background: ${colors.accentSoft};

  .label {
    font-family: ${FONT_SANS};
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: ${colors.accent};
  }

  .text {
    font-family: ${FONT_SANS};
    font-size: 15px;
    line-height: 1.55;
    color: ${colors.ink};
  }
`;

const TeamGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  margin-top: 12px;

  @media (min-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const TeamCard = styled.div`
  padding: 20px;
  border: 1px solid ${colors.border};
  border-radius: 16px;
  background: ${colors.bgAlt};

  .name {
    font-family: ${FONT_SANS};
    font-size: 17px;
    font-weight: 800;
    color: ${colors.ink};
    margin: 0;
  }

  .role {
    font-family: ${FONT_SANS};
    font-size: 13px;
    font-weight: 700;
    color: ${colors.accent};
    margin: 4px 0 10px;
  }

  .bio {
    font-family: ${FONT_SANS};
    font-size: 15px;
    line-height: 1.6;
    color: ${colors.inkSoft};
    margin: 0;
  }
`;

const LinkedInLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 12px;
  font-family: ${FONT_SANS};
  font-size: 14px;
  font-weight: 700;
  color: ${colors.accent};
  text-decoration: none;

  svg {
    font-size: 16px;
  }

  &:hover {
    text-decoration: underline;
  }
`;

function AboutPage() {
  const { t } = useLanguage();
  const about = t.about;

  return (
    <>
      <SiteHeader />
      <PolicyPage>
        <h1>{about.hero.title}</h1>
        <Subtitle>{about.hero.subtitle}</Subtitle>

        <h2>{about.business.title}</h2>
        {about.business.body.map((para, i) => (
          <p key={i}>{para}</p>
        ))}

        <h3>{about.business.audienceTitle}</h3>
        <ul>
          {about.business.audience.map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ul>

        <h3>{about.business.marketTitle}</h3>
        <p>{about.business.market}</p>

        <h2>{about.product.title}</h2>
        <p>{about.product.body}</p>
        <ul>
          {about.product.features.map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ul>

        <h3>{about.product.stageTitle}</h3>
        <StageList>
          {about.product.stages.map((stage, i) => (
            <StageItem key={i}>
              <span className="label">{stage.label}</span>
              <span className="text">{stage.text}</span>
            </StageItem>
          ))}
        </StageList>

        <h2>{about.team.title}</h2>
        <p>{about.team.intro}</p>
        <TeamGrid>
          {about.team.members.map((member, i) => (
            <TeamCard key={i}>
              <p className="name">{member.name}</p>
              <p className="role">{member.role}</p>
              <p className="bio">{member.bio}</p>
              {member.linkedin ? (
                <LinkedInLink
                  href={member.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`${member.name} · ${about.team.linkedinLabel}`}
                >
                  <FaLinkedin />
                  {about.team.linkedinLabel}
                </LinkedInLink>
              ) : null}
            </TeamCard>
          ))}
        </TeamGrid>
        <p className="muted">{about.team.note}</p>

        <h2>{about.contact.title}</h2>
        <p>
          {about.contact.body}
          <a href="mailto:support@onmyway.city">support@onmyway.city</a>
        </p>
      </PolicyPage>
      <SiteFooter />
    </>
  );
}

export default AboutPage;
